import React from 'react';
import { Button } from '@/components/ui/button';
import { usePlan } from '@/context/PlanContext';
import { Lock, AlertTriangle, Eye, EyeOff } from 'lucide-react';

interface PlanLimitBadgeProps {
  resourceType: 'showcases' | 'images' | 'banners' | 'toursEvents' | 'awards' | 'themes' | 'categories';
  showBadge?: boolean;
}

export const PlanLimitBadge: React.FC<PlanLimitBadgeProps> = ({ resourceType, showBadge = true }) => {
  const { getUsage, isPaidPlan } = usePlan();
  const usage = getUsage(resourceType);

  if (isPaidPlan || !usage || !showBadge) return null;

  const { current, limit } = usage;
  const isAtLimit = current >= limit && limit !== -1;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
      isAtLimit 
        ? 'bg-red-100 text-red-700 border border-red-200' 
        : 'bg-amber-100 text-amber-700 border border-amber-200'
    }`}>
      {current}/{limit === -1 ? '∞' : limit}
      {isAtLimit && <Lock className="w-3 h-3" />}
    </span>
  );
};

interface PlanGatedButtonProps {
  resourceType: 'showcases' | 'images' | 'banners' | 'toursEvents' | 'awards' | 'themes' | 'categories';
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
  variant?: 'default' | 'outline' | 'secondary' | 'ghost' | 'link' | 'destructive';
}

export const PlanGatedButton: React.FC<PlanGatedButtonProps> = ({ 
  resourceType, 
  children, 
  onClick,
  className,
  variant = 'default'
}) => {
  const { canAdd, getUsage, openUpgradeModal, isPaidPlan } = usePlan();
  const usage = getUsage(resourceType);
  const isAllowed = canAdd(resourceType);

  const handleClick = () => {
    if (isAllowed) {
      onClick?.();
    } else {
      openUpgradeModal(resourceType);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Button 
        onClick={handleClick} 
        className={className}
        variant={isAllowed ? variant : 'outline'}
      >
        {!isAllowed && <Lock className="w-4 h-4 mr-1" />}
        {children}
      </Button>
      {!isPaidPlan && usage && usage.limit !== -1 && (
        <PlanLimitBadge resourceType={resourceType} />
      )}
    </div>
  );
};

interface GracePeriodBannerProps {
  className?: string;
}

export const GracePeriodBanner: React.FC<GracePeriodBannerProps> = ({ className }) => {
  const { isInGracePeriod, planInfo, openUpgradeModal } = usePlan();

  if (!isInGracePeriod) return null;

  return (
    <div className={`flex items-center justify-between gap-4 p-3 mb-4 bg-amber-50 border border-amber-200 rounded-lg ${className}`}>
      <div className="flex items-center gap-2 text-amber-800">
        <AlertTriangle className="w-5 h-5 flex-shrink-0" />
        <span className="text-sm">
          Your Pro plan has expired. You have <strong>{planInfo?.gracePeriodDaysRemaining} days</strong> remaining to renew before losing access to Pro features.
        </span>
      </div>
      <Button size="sm" onClick={() => openUpgradeModal()}>
        Renew Now
      </Button>
    </div>
  );
};

interface UpgradePromptProps {
  resourceType: string;
  message?: string;
}

export const UpgradePrompt: React.FC<UpgradePromptProps> = ({ resourceType, message }) => {
  const { openUpgradeModal, isPaidPlan } = usePlan();

  if (isPaidPlan) return null;

  const resourceLabels: Record<string, string> = {
    showcases: 'Showcase Songs',
    images: 'Image Gallery',
    banners: 'Promo Banners',
    toursEvents: 'Tours & Events',
    awards: 'Awards',
    themes: 'Themes',
    categories: 'Custom Categories',
    contactLeadsAccess: 'Website Leads',
  };

  return (
    <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-lg">
      <Lock className="w-5 h-5 text-amber-600 flex-shrink-0" />
      <div className="flex-1">
        <p className="text-sm text-amber-800">
          {message || `You've reached the limit for ${resourceLabels[resourceType] || resourceType}. Upgrade to Pro Max for unlimited access.`}
        </p>
      </div>
      <Button size="sm" onClick={() => openUpgradeModal(resourceType)} className="flex-shrink-0">
        Upgrade
      </Button>
    </div>
  );
};

export default { PlanLimitBadge, PlanGatedButton, GracePeriodBanner, UpgradePrompt };

/**
 * Badge shown on each list item row in CMS to indicate whether the item
 * is visible on the website under the current plan limit or hidden/locked.
 */
interface PlanItemBadgeProps {
  resourceType: 'showcases' | 'images' | 'banners' | 'toursEvents' | 'awards' | 'themes' | 'categories';
  index: number; // 0-based index in the order-sorted list
}

export const PlanItemBadge: React.FC<PlanItemBadgeProps> = ({ resourceType, index }) => {
  const { isItemVisibleOnWebsite, isPaidPlan } = usePlan();

  // Unlimited plans do not need per-item visibility badges.
  if (isPaidPlan) return null;

  const visible = isItemVisibleOnWebsite(resourceType, index);

  if (visible) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 border border-green-200" title="Visible on website">
        <Eye className="w-3 h-3" />
        Live
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200" title="Hidden on website — upgrade to make visible">
      <EyeOff className="w-3 h-3" />
      Hidden
    </span>
  );
};

/**
 * Row wrapper that applies opacity styling to locked items in the CMS.
 */
interface PlanItemRowProps {
  resourceType: 'showcases' | 'images' | 'banners' | 'toursEvents' | 'awards' | 'themes' | 'categories';
  index: number;
  children: React.ReactNode;
  className?: string;
}

export const PlanItemRow: React.FC<PlanItemRowProps> = ({ resourceType, index, children, className = '' }) => {
  const { isItemVisibleOnWebsite, isPaidPlan } = usePlan();

  if (isPaidPlan) {
    return <>{children}</>;
  }

  const visible = isItemVisibleOnWebsite(resourceType, index);

  return (
    <div className={`${className} ${!visible ? 'opacity-50' : ''}`}>
      {children}
    </div>
  );
};
